import { useMemo, useState } from 'react'
import { View, Text, TouchableOpacity, Pressable, Modal, ScrollView, StyleSheet } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { useTranslation } from 'react-i18next'
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons'
import { fonts } from '../../lib/theme'
import { useThemeColors } from '../../lib/useThemeColors'
import { useAvatars } from '../../lib/useAvatars'
import PersonAvatarFace from '../PersonAvatarFace'
import PersonAvatarBust from '../PersonAvatarBust'
import { legibleOn } from '../../lib/contrast'

// ─── Scout (gare di un giorno) ────────────────────────────────────────────
// Tre schede sempre visibili sopra la matrice delle cinquine (piu scelto, originalita', gemello) e, al tocco,
// un foglio con il dettaglio. Tutto calcolato dalle cinquine gia caricate: nessuna richiesta in piu.

function buildScout({ rows, stats, meRow }) {
  const nSlots = rows.slots.length
  const { counts, owners, nameOf, keyOf, total } = stats
  const list = Object.keys(counts).map(k => ({
    key: k, count: counts[k], pct: Math.round((100 * counts[k]) / total),
    short: nameOf[k]?.short ?? '', full: nameOf[k]?.full ?? '', owners: owners[k] ?? [],
  })).sort((a, b) => b.count - a.count || a.short.localeCompare(b.short))

  const keysOf = (r) => new Set(rows.slots.map(sl => keyOf(r.id_user, sl, r.picks[sl])).filter(Boolean))
  const myKeys = meRow ? [...keysOf(meRow)] : []
  const byKey = Object.fromEntries(list.map(x => [x.key, x]))

  let originality = null
  if (meRow && myKeys.length) {
    const avg = myKeys.reduce((a, k) => a + (byKey[k]?.pct ?? 0), 0) / myKeys.length
    originality = Math.round(100 - avg)
  }

  // somiglianza con gli altri: quanti corridori in comune con me
  const mine = new Set(myKeys)
  const others = meRow ? rows.pivot.filter(r => r !== meRow).map(r => {
    const ks = keysOf(r)
    let shared = 0; ks.forEach(k => { if (mine.has(k)) shared++ })
    return { row: r, shared }
  }) : []
  const twins = [...others].sort((a, b) => b.shared - a.shared || a.row.name.localeCompare(b.row.name)).filter(o => o.shared > 0)
  // "chi ha giocato come te": gruppi per numero di corridori in comune, dal piu simile (5 su 5) in giu; 0 in comune a parte
  const likeYou = []
  for (let n = nSlots; n >= 1; n--) {
    const players = others.filter(o => o.shared === n).map(o => o.row).sort((a, b) => a.name.localeCompare(b.name))
    if (players.length) likeYou.push({ shared: n, players })
  }
  const noCommon = others.filter(o => o.shared === 0).map(o => o.row).sort((a, b) => a.name.localeCompare(b.name))

  // cinquine identiche
  const groups = {}
  rows.pivot.forEach(r => { const sig = [...keysOf(r)].sort().join('|'); if (sig) (groups[sig] ??= []).push(r) })
  const identical = Object.values(groups).filter(g => g.length > 1)
  const identicalOthers = meRow ? identical.filter(g => !g.includes(meRow)) : identical

  const outsiders = list.filter(x => x.count === 1)
  return { list, myKeys: myKeys.map(k => byKey[k]).filter(Boolean), originality, twins, likeYou, noCommon, identical, identicalOthers, outsiders, byKey }
}

const originalityWord = (o, t) => o >= 60 ? t('pick.scout2_bold') : o >= 35 ? t('pick.scout2_balanced') : t('pick.scout2_aligned')

// tour: { jerseyInfo } per le gare a tappe (maglie di classifica); con un solo corridore a testa le schede cambiano
export default function OdrScout({ rows, stats, meRow, onPickRider, result, tour }) {
  const { t } = useTranslation()
  const tc = useThemeColors()
  const [open, setOpen] = useState(false)
  const m = useMemo(() => buildScout({ rows, stats, meRow }), [rows, stats, meRow])
  const { total } = stats
  const top = m.list[0]
  const twin = m.twins[0]
  const nSlots = rows.slots.length
  const same = m.likeYou[0]?.shared === nSlots ? m.likeYou[0].players : []   // chi ha la tua stessa cinquina (5 su 5)
  const twinAvatars = useAvatars(same.length ? same.slice(0, 3).map(r => r.id_user) : twin ? [twin.row.id_user] : [])
  const tourSingle = !!tour && nSlots === 1

  // tappe in linea: "dove sei" (in fuga / gruppetto / gruppo) e cosa ha scelto il leader
  const myGroup = tourSingle && meRow ? m.myKeys[0] : null
  const level = myGroup ? (myGroup.count === 1 ? 'break' : myGroup.pct <= 25 ? 'small' : 'group') : null
  const leader = (() => {
    if (!tour?.jerseyInfo) return null
    const uid = Object.keys(tour.jerseyInfo).find(u => tour.jerseyInfo[u].kind === 'gc')
    const row = uid ? rows.pivot.find(r => r.id_user === uid) : null
    if (!row) return null
    const key = stats.keyOf(row.id_user, rows.slots[0], row.picks[rows.slots[0]])
    return { row, ji: tour.jerseyInfo[uid], rider: stats.nameOf[key]?.short ?? '—', key }
  })()
  const leaderAvatars = useAvatars(leader ? [leader.row.id_user] : [])

  if (!total) return null

  return (
    <>
      <View style={s.cards}>
        {/* 1. piu scelto */}
        <TouchableOpacity style={[s.card, { backgroundColor: tc.bgCard, borderColor: tc.border }]} onPress={() => setOpen(true)} activeOpacity={0.8} accessibilityRole="button" accessibilityLabel={`${t('pick.share_most_picked')}: ${top?.short}`}>
          <Text style={[s.label, { color: tc.textMuted }]}>{t('pick.share_most_picked')}</Text>
          <Text style={[s.value, { color: tc.text }]} numberOfLines={1} adjustsFontSizeToFit minimumFontScale={0.7}>{top?.short ?? '—'}</Text>
          <Text style={[s.sub, { color: tc.textMuted }]}>{top ? `${t('pick.sheet_chosen_by', { count: top.count, total })} · ${top.pct}%` : ''}</Text>
          <View style={[s.track, { backgroundColor: tc.border }]}><View style={[s.fill, { backgroundColor: tc.rosa, width: `${top?.pct ?? 0}%` }]} /></View>
        </TouchableOpacity>

        {/* 2. tappe in linea: dove sei; altrimenti originalita' (o, senza cinquina, scelte uniche) */}
        {myGroup ? (
          <TouchableOpacity style={[s.card, { backgroundColor: tc.bgCard, borderColor: tc.border }]} onPress={() => setOpen(true)} activeOpacity={0.8} accessibilityRole="button">
            <Text style={[s.label, { color: tc.textMuted }]}>{t('pick.scout_t_position')}</Text>
            <Text style={[s.value, { color: tc.text }]}>{t(`pick.scout_t_${level}`)}</Text>
            <Text style={[s.sub, { color: tc.rosaText, fontFamily: fonts.condensedBold }]} numberOfLines={2}>
              {myGroup.count === 1 ? t('pick.scout_t_alone') : t('pick.scout_t_with', { count: myGroup.count - 1 })}
            </Text>
          </TouchableOpacity>
        ) : (
        <TouchableOpacity style={[s.card, { backgroundColor: tc.bgCard, borderColor: tc.border }]} onPress={() => setOpen(true)} activeOpacity={0.8} accessibilityRole="button">
          <Text style={[s.label, { color: tc.textMuted }]}>{m.originality !== null ? t('pick.scout2_originality') : t('pick.share_unique_picks')}</Text>
          <Text style={[s.value, { color: tc.text }]}>{m.originality !== null ? `${m.originality}%` : m.outsiders.length}</Text>
          <Text style={[s.sub, { color: tc.rosaText, fontFamily: fonts.condensedBold }]} numberOfLines={1}>
            {m.originality !== null ? originalityWord(m.originality, t) : t('pick.share_unique_sub')}
          </Text>
          <View style={[s.track, { backgroundColor: tc.border }]}><View style={[s.fill, { backgroundColor: tc.rosa, width: `${m.originality ?? 0}%` }]} /></View>
        </TouchableOpacity>
        )}

        {/* 3. giri: cosa ha scelto il leader (o quanti corridori diversi); gare di un giorno: gemello/i */}
        {tour ? (
          <TouchableOpacity style={[s.card, { backgroundColor: tc.bgCard, borderColor: tc.border }]} onPress={() => setOpen(true)} activeOpacity={0.8} accessibilityRole="button">
            <Text style={[s.label, { color: tc.textMuted }]}>{leader ? t('pick.scout_t_leader') : t('pick.share_distinct_riders')}</Text>
            {leader ? (
              <>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                  <PersonAvatarBust userId={leader.row.id_user} settings={leaderAvatars[leader.row.id_user]} jersey={leader.ji} width={34} height={36} />
                  <Text style={[s.valueSm, { color: tc.text }]} numberOfLines={2} adjustsFontSizeToFit minimumFontScale={0.7}>{leader.rider}</Text>
                </View>
                <Text style={[s.sub, { color: tc.textMuted }]} numberOfLines={1}>{leader.row.name}</Text>
              </>
            ) : (
              <>
                <Text style={[s.value, { color: tc.text }]}>{m.list.length}</Text>
                <Text style={[s.sub, { color: tc.textMuted }]}>{t('pick.scout_players', { count: total })}</Text>
              </>
            )}
          </TouchableOpacity>
        ) : (
        <TouchableOpacity style={[s.card, { backgroundColor: tc.bgCard, borderColor: tc.border }]} onPress={() => setOpen(true)} activeOpacity={0.8} accessibilityRole="button">
          <Text style={[s.label, { color: tc.textMuted }]}>{!meRow ? t('pick.scout2_identical') : same.length > 1 ? t('pick.scout2_twins_card') : t('pick.scout2_twin')}</Text>
          {meRow ? (
            same.length > 1 ? (
              <>
                <View style={{ flexDirection: 'row', alignItems: 'center', paddingLeft: 6 }}>
                  {same.slice(0, 3).map((r, i) => (
                    <View key={r.name} style={{ marginLeft: -6, zIndex: 3 - i }}>
                      <PersonAvatarFace userId={r.id_user} settings={twinAvatars[r.id_user]} size={26} />
                    </View>
                  ))}
                  <Text style={[s.value, { color: tc.text, marginTop: 0, marginLeft: 8 }]}>{same.length}</Text>
                </View>
                <Text style={[s.sub, { color: tc.textMuted }]} numberOfLines={2}>{t('pick.scout2_same_as_you', { count: same.length })}</Text>
              </>
            ) : twin ? (
              <>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                  <PersonAvatarFace userId={twin.row.id_user} settings={twinAvatars[twin.row.id_user]} size={24} />
                  <Text style={[s.valueSm, { color: tc.text }]} numberOfLines={1} adjustsFontSizeToFit minimumFontScale={0.7}>{twin.row.name}</Text>
                </View>
                <Text style={[s.sub, { color: tc.textMuted }]}>{t('pick.scout2_in_common', { count: twin.shared, total: nSlots })}</Text>
              </>
            ) : <Text style={[s.sub, { color: tc.textMuted, marginTop: 6 }]}>{t('pick.scout2_none')}</Text>
          ) : (
            <>
              <Text style={[s.value, { color: tc.text }]}>{m.identical.length}</Text>
              <Text style={[s.sub, { color: tc.textMuted }]}>{t('pick.scout2_identical_sub')}</Text>
            </>
          )}
        </TouchableOpacity>
        )}
      </View>

      {/* Com'e' andata (a gara conclusa) */}
      {result && (result.me || result.winner) && (
        <TouchableOpacity style={[s.wide, { backgroundColor: tc.bgCard, borderColor: tc.border }]} onPress={() => setOpen(true)} activeOpacity={0.8} accessibilityRole="button" accessibilityLabel={t('pick.result_title')}>
          <MaterialCommunityIcons name="flag-checkered" size={20} color={tc.rosaText} />
          <View style={{ flex: 1, minWidth: 0, gap: 2 }}>
            <Text style={[s.label, { color: tc.textMuted }]}>{t('pick.result_title').toUpperCase()}</Text>
            {result.me?.best && (
              <Text style={[s.wideLine, { color: tc.text }]} numberOfLines={1}>
                {t('pick.result_best')}: <Text style={{ fontFamily: fonts.condensedBold }}>{surname(result.me.best.name)}</Text> <Text style={{ color: result.me.best.pts >= 0 ? tc.success : tc.rosaText }}>{signed(result.me.best.pts)} pt</Text>
              </Text>
            )}
            {result.difference && (
              <Text style={[s.wideLine, { color: tc.text }]} numberOfLines={1}>
                {t('pick.result_difference')}: <Text style={{ fontFamily: fonts.condensedBold }}>{surname(result.difference.name)}</Text>
              </Text>
            )}
          </View>
          <MaterialCommunityIcons name="chevron-right" size={18} color={tc.textMuted} />
        </TouchableOpacity>
      )}

      {open && <ScoutSheet tour={tour} tourSingle={tourSingle} result={result} model={m} rows={rows} stats={stats} meRow={meRow} onPickRider={(k) => { setOpen(false); onPickRider?.(k) }} onClose={() => setOpen(false)} />}
    </>
  )
}

const surname = (label) => String(label ?? '').trim().split(/\s+/)[0] || ''
const signed = (n) => (n > 0 ? `+${n}` : String(n))

function ResultSection({ result, total, avatars, tc }) {
  const { t } = useTranslation()
  const { me, winner, difference, flop } = result
  const OwnerChips = ({ list }) => (
    <View style={s.chips}>
      {list.slice(0, 10).map(r => (
        <View key={r.name} style={[s.chip, { backgroundColor: tc.bg, borderColor: tc.border }]}>
          <PersonAvatarFace userId={r.id_user} settings={avatars[r.id_user]} size={22} />
          <Text style={[s.chipText, { color: tc.text }]} numberOfLines={1}>{r.name}</Text>
        </View>
      ))}
      {list.length > 10 && <Text style={[s.secSub, { color: tc.textMuted, alignSelf: 'center' }]}>+{list.length - 10}</Text>}
    </View>
  )
  const Block = ({ title, name, sub, owners, accent }) => (
    <View style={[s.group, { backgroundColor: tc.bgCard2, borderColor: accent ? tc.rosa : tc.border }]}>
      <Text style={[s.label, { color: accent ? tc.rosaText : tc.textMuted }]}>{title.toUpperCase()}</Text>
      <Text style={[s.popName, { color: tc.text, fontSize: 16 }]} numberOfLines={1}>{name}</Text>
      {!!sub && <Text style={[s.secSub, { color: tc.textMuted }]}>{sub}</Text>}
      {owners && owners.length > 0 && <OwnerChips list={owners} />}
    </View>
  )
  return (
    <Section title={t('pick.result_title')}>
      {me && (
        <View style={[s.group, { backgroundColor: tc.bgCard2, borderColor: tc.rosa }]}>
          <Text style={[s.secTitle, { color: tc.text }]}>{t('pick.result_total', { pts: me.total })}</Text>
          {me.picks.map(k => {
            const isBest = me.best && k.slot === me.best.slot && k.id === me.best.id
            const isWorst = me.worst && k.slot === me.worst.slot && k.id === me.worst.id
            return (
              <View key={k.slot} style={[s.resRow, { borderColor: tc.border }]}>
                <View style={[s.slot, { backgroundColor: tc.bgCard3 }]}><Text style={[s.slotText, { color: tc.textMuted }]}>{k.slot}</Text></View>
                <View style={{ flex: 1, minWidth: 0 }}>
                  <Text style={[s.popName, { color: tc.text }]} numberOfLines={1}>{surname(k.name) || '—'}</Text>
                  <Text style={[s.secSub, { color: tc.textMuted }]}>
                    {k.rank ? t('pick.result_pick_line', { slot: k.slot, rank: k.rank }) : t('pick.result_dnf')}
                    {isBest ? ` · ★ ${t('pick.result_best')}` : isWorst ? ` · ${t('pick.result_worst')}` : ''}
                  </Text>
                </View>
                <Text style={[s.resPts, { color: k.pts >= 0 ? tc.success : tc.rosaText }]}>{signed(k.pts)}</Text>
              </View>
            )
          })}
          {me.bonus.length > 0 && (
            <Text style={[s.secSub, { color: tc.textMuted, marginTop: 4 }]}>
              {me.bonus.map(b => `${b.kind === 'malus' ? t('pick.result_malus') : b.kind === 'top10' ? t('pick.result_bonus_top10') : t('pick.result_bonus_top15')} ${signed(b.value)}`).join('  ·  ')}
            </Text>
          )}
        </View>
      )}
      {winner && (
        <Block
          title={t('pick.result_winner')}
          name={winner.name || '—'}
          sub={winner.owners.length ? t('pick.result_winner_owners', { count: winner.owners.length, total }) : t('pick.result_nobody')}
          owners={winner.owners.map(o => o.row)}
        />
      )}
      {difference && (
        <Block accent title={t('pick.result_difference')} name={difference.name} sub={t('pick.result_difference_sub', { delta: signed(difference.delta), count: difference.owners.length })} owners={difference.owners} />
      )}
      {flop && (
        <Block title={t('pick.result_flop')} name={flop.name} sub={t('pick.result_flop_sub', { delta: signed(flop.delta), count: flop.owners.length })} owners={flop.owners} />
      )}
    </Section>
  )
}

function Section({ title, sub, children }) {
  const tc = useThemeColors()
  return (
    <View style={{ marginBottom: 20 }}>
      <Text style={[s.secTitle, { color: tc.text }]}>{title}</Text>
      {!!sub && <Text style={[s.secSub, { color: tc.textMuted }]}>{sub}</Text>}
      <View style={{ gap: 6, marginTop: 8 }}>{children}</View>
    </View>
  )
}

function PlayerLine({ row, avatars, right, tc }) {
  return (
    <View style={[s.line, { backgroundColor: tc.bgCard2, borderColor: tc.border }]}>
      <PersonAvatarFace userId={row.id_user} settings={avatars[row.id_user]} size={30} />
      <Text style={[s.lineName, { color: tc.text }]} numberOfLines={1}>{row.name}</Text>
      {right}
    </View>
  )
}

function ScoutSheet({ tour, tourSingle, result, model: m, rows, stats, meRow, onPickRider, onClose }) {
  const { t } = useTranslation()
  const tc = useThemeColors()
  const insets = useSafeAreaInsets()
  const { total } = stats
  const nSlots = rows.slots.length
  const avatars = useAvatars(rows.pivot.map(r => r.id_user))
  const mineKeys = new Set(m.myKeys.map(x => x.key))
  const nameList = (arr, max = 4) => arr.slice(0, max).map(o => o.name).join(', ') + (arr.length > max ? ` +${arr.length - max}` : '')

  return (
    <Modal visible transparent animationType="slide" onRequestClose={onClose}>
      <View style={s.overlay}>
        <Pressable style={StyleSheet.absoluteFill} onPress={onClose} accessibilityRole="button" accessibilityLabel={t('pick.close')} />
        <View style={[s.sheet, { backgroundColor: tc.bg, paddingBottom: insets.bottom + 12 }]}>
          <View style={[s.header, { borderBottomColor: tc.border }]}>
            <MaterialCommunityIcons name="radar" size={22} color={tc.rosaText} />
            <Text style={[s.title, { color: tc.text }]}>{t('pick.scout2_title')}</Text>
            <Pressable onPress={onClose} hitSlop={6} style={[s.close, { backgroundColor: tc.bgCard2 }]} accessibilityRole="button" accessibilityLabel={t('pick.close')}>
              <MaterialCommunityIcons name="close" size={18} color={tc.text} />
            </Pressable>
          </View>
          <ScrollView contentContainerStyle={{ padding: 16 }}>
            {/* com'e' andata */}
            {result && <ResultSection result={result} total={total} avatars={avatars} tc={tc} />}

            {/* popolarita' */}
            <Section title={t('pick.scout2_popularity')} sub={t('pick.scout2_tap_hint')}>
              {m.list.map(x => (
                <TouchableOpacity key={x.key} onPress={() => onPickRider(x.key)} activeOpacity={0.7} style={[s.popRow, { backgroundColor: tc.bgCard2, borderColor: mineKeys.has(x.key) ? tc.rosa : tc.border }]} accessibilityRole="button" accessibilityLabel={`${x.short}, ${t('pick.sheet_chosen_by', { count: x.count, total })}`}>
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <Text style={[s.popName, { color: tc.text }]} numberOfLines={1}>{x.short}{mineKeys.has(x.key) ? '  ★' : ''}</Text>
                    <View style={[s.track, { backgroundColor: tc.border, marginTop: 4 }]}><View style={[s.fill, { backgroundColor: tc.rosa, width: `${x.pct}%` }]} /></View>
                  </View>
                  <Text style={[s.popPct, { color: tc.textMuted }]}>{x.count} · {x.pct}%</Text>
                </TouchableOpacity>
              ))}
            </Section>

            {/* le tue scelte */}
            {meRow && (
              <Section title={t('pick.scout2_your_picks')}>
                {m.myKeys.map(x => {
                  const others = x.owners.filter(o => o.id_user !== meRow.id_user)
                  return (
                    <View key={x.key} style={[s.line, { backgroundColor: tc.bgCard2, borderColor: tc.border, alignItems: 'flex-start' }]}>
                      <View style={{ flex: 1, minWidth: 0 }}>
                        <Text style={[s.lineName, { color: tc.text, flex: 0 }]} numberOfLines={1}>{x.full || x.short}</Text>
                        <Text style={[s.secSub, { color: tc.textMuted, marginTop: 2 }]} numberOfLines={2}>
                          {others.length ? t('pick.scout2_also_by', { names: nameList(others) }) : t('pick.scout2_only_you')}
                        </Text>
                      </View>
                      <Text style={[s.popPct, { color: tc.textMuted }]}>{x.count} · {x.pct}%</Text>
                    </View>
                  )
                })}
              </Section>
            )}

            {/* chi ha giocato come te (non per le tappe con un solo corridore: lo dice gia 'Le tue scelte') */}
            {meRow && !tourSingle && (
              <Section title={t('pick.scout2_like_you')}>
                {m.likeYou.map(g => (
                  <View key={g.shared} style={[s.group, { backgroundColor: tc.bgCard2, borderColor: g.shared === nSlots ? tc.rosa : tc.border }]}>
                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Text style={[s.popName, { color: g.shared === nSlots ? tc.rosaText : tc.text }]}>
                        {g.shared === nSlots ? t('pick.scout2_same_picks') : t('pick.scout2_in_common', { count: g.shared, total: nSlots })}
                      </Text>
                      <Text style={[s.popPct, { color: tc.textMuted }]}>{t('pick.scout2_group_count', { count: g.players.length })}</Text>
                    </View>
                    <View style={s.chips}>
                      {g.players.map(r => (
                        <View key={r.name} style={[s.chip, { backgroundColor: tc.bg, borderColor: tc.border }]}>
                          <PersonAvatarFace userId={r.id_user} settings={avatars[r.id_user]} size={22} />
                          <Text style={[s.chipText, { color: tc.text }]} numberOfLines={1}>{r.name}</Text>
                        </View>
                      ))}
                    </View>
                  </View>
                ))}
                {m.likeYou.length === 0 && <Text style={[s.secSub, { color: tc.textMuted }]}>{t('pick.scout2_none')}</Text>}
                {m.noCommon.length > 0 && (
                  <Text style={[s.secSub, { color: tc.textMuted, marginTop: 4 }]}>
                    {t('pick.scout2_no_common')}: {nameList(m.noCommon.map(r => ({ name: r.name })), 6)}
                  </Text>
                )}
              </Section>
            )}

            {/* maglie: cosa hanno scelto i leader */}
            {tour?.jerseyInfo && Object.keys(tour.jerseyInfo).length > 0 && (
              <Section title={t('pick.scout_t_jerseys')}>
                {['gc', 'pt', 'gpm'].map(kind => {
                  const uid = Object.keys(tour.jerseyInfo).find(u => tour.jerseyInfo[u].kind === kind)
                  const row = uid ? rows.pivot.find(r => r.id_user === uid) : null
                  if (!row) return null
                  const ji = tour.jerseyInfo[uid]
                  const picks = rows.slots.map(sl => stats.nameOf[stats.keyOf(row.id_user, sl, row.picks[sl])]?.short).filter(Boolean).join(', ')
                  return (
                    <View key={kind} style={[s.line, { backgroundColor: tc.bgCard2, borderColor: legibleOn(ji.color, tc.bgCard2, 1.6) }]}>
                      <PersonAvatarBust userId={row.id_user} settings={avatars[row.id_user]} jersey={ji} width={40} height={44} />
                      <View style={{ flex: 1, minWidth: 0 }}>
                        <Text style={[s.secSub, { color: legibleOn(ji.color, tc.bgCard2, 3), fontFamily: fonts.condensedBold }]} numberOfLines={1}>{ji.label}</Text>
                        <Text style={[s.lineName, { color: tc.text, flex: 0 }]} numberOfLines={1}>{row.name}</Text>
                      </View>
                      <Text style={[s.popName, { color: tc.text, flexShrink: 1, textAlign: 'right' }]} numberOfLines={2}>{picks || '—'}</Text>
                    </View>
                  )
                })}
              </Section>
            )}

            {/* outsider */}
            <Section title={tour ? t('pick.scout_t_breakaway') : t('pick.scout2_outsiders')} sub={t('pick.share_unique_sub')}>
              {m.outsiders.map(x => {
                const owner = x.owners[0]
                const row = rows.pivot.find(r => r.id_user === owner?.id_user)
                return row ? (
                  <PlayerLine key={x.key} row={row} avatars={avatars} tc={tc} right={<Text style={[s.popName, { color: tc.text, flexShrink: 1 }]} numberOfLines={1}>{x.short}</Text>} />
                ) : null
              })}
              {m.outsiders.length === 0 && <Text style={[s.secSub, { color: tc.textMuted }]}>{t('pick.scout2_none')}</Text>}
            </Section>

            {/* cinquine identiche (tra gli altri) */}
            {!tourSingle && <Section title={meRow ? t('pick.scout2_other_identical') : t('pick.scout2_identical')}>
              {m.identicalOthers.map((g, i) => (
                <View key={i} style={[s.line, { backgroundColor: tc.bgCard2, borderColor: tc.border }]}>
                  <MaterialCommunityIcons name="account-multiple-outline" size={20} color={tc.rosaText} />
                  <Text style={[s.lineName, { color: tc.text }]} numberOfLines={3}>{g.map(r => r.name).join(', ')}</Text>
                </View>
              ))}
              {m.identicalOthers.length === 0 && <Text style={[s.secSub, { color: tc.textMuted }]}>{t('pick.scout_no_identical')}</Text>}
            </Section> }
          </ScrollView>
        </View>
      </View>
    </Modal>
  )
}

const s = StyleSheet.create({
  cards: { flexDirection: 'row', gap: 8, paddingHorizontal: 12, paddingTop: 8, paddingBottom: 6 },
  card: { flex: 1, minHeight: 96, borderWidth: 1, borderRadius: 12, padding: 10, justifyContent: 'space-between' },
  label: { fontSize: 9, letterSpacing: 1.5, fontFamily: fonts.condensedBold },
  value: { fontSize: 20, textTransform: 'uppercase', fontFamily: fonts.condensedBold, marginTop: 2 },
  valueSm: { flex: 1, fontSize: 15, fontFamily: fonts.condensedBold },
  sub: { fontSize: 11, marginTop: 1 },
  track: { height: 4, borderRadius: 2, overflow: 'hidden', marginTop: 6 },
  fill: { height: 4, borderRadius: 2 },
  overlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.6)', justifyContent: 'flex-end' },
  sheet: { borderTopLeftRadius: 20, borderTopRightRadius: 20, maxHeight: '92%', minHeight: '60%' },
  header: { flexDirection: 'row', alignItems: 'center', gap: 10, padding: 16, borderBottomWidth: 0.5 },
  title: { flex: 1, fontFamily: fonts.condensedBold, fontSize: 20 },
  close: { width: 32, height: 32, borderRadius: 16, alignItems: 'center', justifyContent: 'center' },
  secTitle: { fontFamily: fonts.condensedBold, fontSize: 16, textTransform: 'uppercase', letterSpacing: 0.5 },
  secSub: { fontSize: 12 },
  popRow: { flexDirection: 'row', alignItems: 'center', gap: 10, borderWidth: 1, borderRadius: 10, paddingHorizontal: 10, paddingVertical: 8, minHeight: 48 },
  popName: { fontFamily: fonts.condensedBold, fontSize: 14, textTransform: 'uppercase' },
  popPct: { fontSize: 12, fontFamily: fonts.condensedBold },
  line: { flexDirection: 'row', alignItems: 'center', gap: 10, borderWidth: 1, borderRadius: 10, padding: 10, minHeight: 48 },
  wide: { flexDirection: 'row', alignItems: 'center', gap: 10, marginHorizontal: 12, marginTop: 2, marginBottom: 4, padding: 12, minHeight: 56, borderWidth: 1, borderRadius: 12 },
  wideLine: { fontSize: 12 },
  resRow: { flexDirection: 'row', alignItems: 'center', gap: 10, borderTopWidth: StyleSheet.hairlineWidth, paddingTop: 8, marginTop: 4 },
  slot: { width: 26, height: 26, borderRadius: 13, alignItems: 'center', justifyContent: 'center' },
  slotText: { fontFamily: fonts.condensedBold, fontSize: 13 },
  resPts: { fontFamily: fonts.condensedBold, fontSize: 18, minWidth: 40, textAlign: 'right' },
  group: { borderWidth: 1, borderRadius: 12, padding: 10, gap: 8 },
  chips: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  chip: { flexDirection: 'row', alignItems: 'center', gap: 6, borderWidth: 1, borderRadius: 16, paddingVertical: 3, paddingLeft: 3, paddingRight: 10, maxWidth: '100%' },
  chipText: { fontSize: 12, fontFamily: fonts.condensedBold, flexShrink: 1 },
  lineName: { flex: 1, fontSize: 14, fontFamily: fonts.condensedBold },
})
